import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Bug, Copy, Check } from "lucide-react";
import { useState } from "react";
import { useEntitlement } from "@/hooks/useEntitlement";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

const BoolBadge = ({ value }: { value: boolean }) =>
  value ? (
    <Badge className="bg-medical-green text-white hover:bg-medical-green/90">true</Badge>
  ) : (
    <Badge variant="secondary">false</Badge>
  );

export default function RoleDiagnostics() {
  const { user } = useAuth();
  const {
    userId,
    isAdmin,
    isRealAdmin,
    isImpersonating,
    hasActiveAccess,
    examSlug,
    subscriptionType,
  } = useEntitlement();
  const { viewAsUser } = useImpersonation();
  const [copied, setCopied] = useState(false);

  const diagnostics = {
    email: user?.email ?? null,
    userId,
    isAdmin,
    isRealAdmin,
    isImpersonating,
    viewAsUser,
    hasActiveAccess,
    examSlug,
    subscriptionType,
    timestamp: new Date().toISOString(),
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
      setCopied(true);
      toast.success("Diagnostics copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const rows: Array<{ label: string; value: React.ReactNode; hint?: string }> = [
    { label: "Email", value: <span className="font-mono text-xs">{user?.email ?? "—"}</span> },
    { label: "User ID", value: <span className="font-mono text-xs break-all">{userId ?? "—"}</span> },
    { label: "isAdmin", value: <BoolBadge value={!!isAdmin} />, hint: "Admin powers visible in UI (false while impersonating)" },
    { label: "isRealAdmin", value: <BoolBadge value={!!isRealAdmin} />, hint: "Underlying admin role in the database" },
    { label: "isImpersonating", value: <BoolBadge value={!!isImpersonating} />, hint: "Real admin currently previewing as a user" },
    { label: "viewAsUser", value: <BoolBadge value={!!viewAsUser} />, hint: "Toggle state from ImpersonationContext" },
    { label: "hasActiveAccess", value: <BoolBadge value={!!hasActiveAccess} /> },
    { label: "examSlug", value: <span className="font-mono text-xs">{examSlug ?? "—"}</span> },
    { label: "subscriptionType", value: <span className="font-mono text-xs">{subscriptionType ?? "—"}</span> },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Role &amp; Access Diagnostics
            </CardTitle>
            <CardDescription>
              Resolved role flags for the currently signed-in account. The "Admin Dashboard"
              button in the user header is shown when{" "}
              <span className="font-mono">isRealAdmin = true</span> AND{" "}
              <span className="font-mono">isImpersonating = false</span>.
            </CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={copy}>
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border divide-y">
          {rows.map((r) => (
            <div key={r.label} className="flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">{r.label}</p>
                {r.hint && <p className="text-xs text-muted-foreground">{r.hint}</p>}
              </div>
              <div className="shrink-0">{r.value}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
